import { ref, watch, computed } from '@vue/composition-api'
import store from '@/store'
import router from '@/router'
import Swal from 'sweetalert2'
import { useUtils as useI18nUtils } from '@core/libs/i18n'
// Notification
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

const { t } = useI18nUtils()

export default function useUsersList() {
  const toast = useToast()
  const yes = t('Yes, delete it!')
  const areYouSure = t('Are you sure?')
  const detail = t("You won't be able to revert this!")
  const cancel = t('Cancel')
  const refUserListTable = ref(null)

  const tableColumns = [
    { key: 'createdAt', class: "cols-1", label: "Registration Date", sortable: true },
    { key: 'membername', class: "col-6", label: "Participant", sortable: true },
    { key: 'detail', class: "col-3", label: "Course", sortable: false },
    { key: 'actions', class: "col-3", label: "Actions", sortable: false },
  ]
  const perPage = ref(10)
  const totalUsers = ref(0)
  const courseName = ref('')
  const currentPage = ref(1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref('')
  const debouncedSearchQuery = ref('')
  const isLoading = ref(false)
  let searchDebounceTimer = null
  const sortBy = ref('membername')
  const isSortDirDesc = ref(true)
  const roleFilter = ref(null)
  const planFilter = ref(null)
  const statusFilter = ref(null)
  const courseId = computed(() => router.currentRoute.params.id)
  const items = ref(null)
  const hasCert = ref(false)
  const hasExam = ref(false)

  const dataMeta = computed(() => {
    const localItemsCount = items.value ? items.value.length : 0
    const from = totalUsers.value === 0 ? 0 : perPage.value * (currentPage.value - 1) + 1
    return {
      from,
      to: totalUsers.value === 0 ? 0 : Math.min(from + localItemsCount - 1, totalUsers.value),
      of: totalUsers.value,
    }
  })

  watch(searchQuery, (newValue) => {
    if (searchDebounceTimer) clearTimeout(searchDebounceTimer)
    searchDebounceTimer = setTimeout(() => {
      debouncedSearchQuery.value = newValue
    }, 300)
  })

  const refetchData = () => {
    fetchCourseSubscribers()
  }

  const getCourseDetailByGuid = () => {
    if (!courseId.value) return

    store.dispatch('apps-participants/fetchCourseDetailByGuid', courseId.value)
      .then(response => {
        const { data } = response.data
        if (data.courseCertified === true) {
          hasCert.value = data.courseCertified
        }
        hasExam.value = data.hasCertificationExam
        if (data.title && !courseName.value) {
          courseName.value = data.title
        }
      })
  }

  const fetchCourseSubscribers = () => {
    if (!courseId.value) {
      items.value = []
      totalUsers.value = 0
      return
    }

    isLoading.value = true
    store
      .dispatch('apps-participants/fetchParticipantsByCourse', {
        courseGuid: courseId.value,
        page: currentPage.value,
        limit: perPage.value,
        search: debouncedSearchQuery.value,
      })
      .then(response => {
        const { data, pagination, success } = response.data
        if (success !== undefined && String(success) !== '100') {
          throw new Error('Unexpected response status')
        }

        items.value = data || []
        totalUsers.value = pagination && pagination.totalRecords ? pagination.totalRecords : 0

        if (items.value.length > 0) {
          courseName.value = items.value[0].coursename
        }

        const totalPages = pagination && pagination.totalPages ? pagination.totalPages : 1
        if (totalUsers.value > 0 && currentPage.value > totalPages) {
          currentPage.value = totalPages
        }
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error fetching users list',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
      .finally(() => {
        isLoading.value = false
      })
  }

  const loadCourseData = () => {
    if (!courseId.value) return
    fetchCourseSubscribers()
    getCourseDetailByGuid()
  }

  watch(courseId, () => {
    currentPage.value = 1
    loadCourseData()
  }, { immediate: true })

  watch(debouncedSearchQuery, () => {
    if (currentPage.value !== 1) {
      currentPage.value = 1
      return
    }
    fetchCourseSubscribers()
  })

  watch([currentPage, perPage], () => {
    fetchCourseSubscribers()
  })

  const removeSubscriberFromCourse = (userData) => {
    Swal.fire({
      title: areYouSure,
      text: detail,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: yes,
      cancelButtonText: cancel,
      customClass: {
        confirmButton: 'btn btn-primary',
        cancelButton: 'btn btn-outline-danger ml-1',
      },
      buttonsStyling: false,
    }).then(result => {
      if (result) {
        if (result.isConfirmed) {
          store.dispatch('apps-participants/removeParticipantFromCourse', userData).then(() => {
            toast({
              component: ToastificationContent,
              props: {
                title: `User removed successfuly`,
                icon: "CheckIcon",
                variant: "success",
              },
            })
            refetchData()
          }).catch(() => {
            toast({
              component: ToastificationContent,
              props: {
                title: 'User could not be removed ',
                icon: 'AlertTriangleIcon',
                variant: 'danger',
              },
            })
          })
        }
      }
    })
  }

  const resolveUserRoleVariant = role => {
    if (role === 'subscriber') return 'primary'
    if (role === 'author') return 'warning'
    if (role === 'maintainer') return 'success'
    if (role === 'editor') return 'info'
    if (role === 'admin') return 'danger'
    return 'primary'
  }

  const resolveUserRoleIcon = role => {
    if (role === 'subscriber') return 'UserIcon'
    if (role === 'author') return 'SettingsIcon'
    if (role === 'maintainer') return 'DatabaseIcon'
    if (role === 'editor') return 'Edit2Icon'
    if (role === 'admin') return 'ServerIcon'
    return 'UserIcon'
  }

  const resolveUserStatusVariant = status => {
    if (status === 0) return 'warning'
    if (status === 1) return 'success'
    if (status === 2) return 'secondary'
    return 'primary'
  }
  const resolveUserStatusVariantText = status => {
    if (status === 0) return 'deleted'
    if (status === 1) return 'active'
    if (status === 2) return 'disabled'
    return 'primary'
  }

  return {
    tableColumns,
    perPage,
    currentPage,
    totalUsers,
    courseName,
    dataMeta,
    perPageOptions,
    searchQuery,
    debouncedSearchQuery,
    isLoading,
    sortBy,
    isSortDirDesc,
    refUserListTable,
    items,
    hasCert,
    hasExam,

    resolveUserRoleVariant,
    resolveUserRoleIcon,
    resolveUserStatusVariant,
    resolveUserStatusVariantText,
    refetchData,

    roleFilter,
    planFilter,
    statusFilter,
    fetchCourseSubscribers,
    removeSubscriberFromCourse,
    courseId,
  }
}
